import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CameraOff } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'

/**
 * Job QRs carry the qr_token uuid; typed codes look like MPG-2026-00005, or
 * AI-2026-00005 with a partner's code in place of MPG (the server maps it back).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CODE_RE = /^[A-Z][A-Z0-9]{1,4}-\d{4}-\d{5}$/i

/**
 * Live camera QR scanner. Decodes with the native BarcodeDetector where the
 * browser has one (Chrome/Android) and falls back to jsQR (lazy-loaded)
 * everywhere else, iPhones included. Job sheets also carry a second artwork
 * QR that encodes a URL - anything that is not a job token or code is
 * rejected with a hint instead of navigating somewhere wrong.
 */
export default function QrScanner({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setHint(null)

    let stream: MediaStream | null = null
    let raf = 0
    let stopped = false
    const canvas = document.createElement('canvas')

    const found = (raw: string) => {
      const value = raw.trim()
      if (UUID_RE.test(value) || CODE_RE.test(value)) {
        stopped = true
        onClose()
        navigate(`/staff/jobs/${encodeURIComponent(value)}`)
        return true
      }
      // Most likely the artwork QR on the same sheet - keep scanning.
      setHint('That is not a job QR - aim at the one next to the job code.')
      return false
    }

    const run = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        })
      } catch {
        setError(
          'Camera unavailable. Allow camera access for this site in your browser settings, or type the job code instead.',
        )
        return
      }
      if (stopped) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      const video = videoRef.current
      if (!video) return
      video.srcObject = stream
      await video.play().catch(() => {})

      // Native detector where available; jsQR (lazy) everywhere else.
      const Detector = (window as unknown as { BarcodeDetector?: new (options: { formats: string[] }) => { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> } }).BarcodeDetector
      const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null
      const jsQR = detector ? null : (await import('jsqr')).default

      const tick = async () => {
        if (stopped) return
        if (video.readyState >= video.HAVE_ENOUGH_DATA) {
          if (detector) {
            const codes = await detector.detect(video).catch(() => [])
            if (codes[0] && found(codes[0].rawValue)) return
          } else if (jsQR) {
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext('2d', { willReadFrequently: true })
            if (ctx) {
              ctx.drawImage(video, 0, 0)
              const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const result = jsQR(image.data, image.width, image.height)
              if (result?.data && found(result.data)) return
            }
          }
        }
        raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)
    }

    run()

    return () => {
      stopped = true
      cancelAnimationFrame(raf)
      stream?.getTracks().forEach((track) => track.stop())
      if (videoRef.current) videoRef.current.srcObject = null
    }
  }, [open, navigate, onClose])

  return (
    <Modal open={open} onClose={onClose} title="Scan job QR">
      {error ? (
        <div className="py-8 text-center">
          <CameraOff className="mx-auto size-8 text-muted" />
          <p className="mx-auto mt-3 max-w-xs text-sm text-muted">{error}</p>
          <Button variant="outline" className="mt-4" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <div>
          <div className="relative overflow-hidden rounded-xl bg-foreground">
            <video ref={videoRef} playsInline muted className="aspect-square w-full object-cover" />
            {/* Aiming frame */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="size-48 rounded-2xl border-2 border-background/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
          </div>
          <p className="mt-3 text-center text-xs text-muted">
            {hint ?? 'Aim at the QR next to the job code on the job sheet.'}
          </p>
        </div>
      )}
    </Modal>
  )
}
